import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

// Models
import { Customer } from '../../../models/customer.model';
import { CustomerFormData } from '../../../models/customer-form-data.model';

// Services
import { CustomerStateService } from '../../../services/customer-state.service';
import { IdGeneratorService } from '../../../services/shared/id-generator/id-generator.service';

// Components
import { CustomerInfoComponent } from '../customer-info/customer-info.component';
import { RegionsManagerComponent, RegionRemovalConfirmedEvent } from '../regions-manager/regions-manager.component';
import { SitesManagerComponent, SiteRemovalRequestEvent, SiteRemovalConfirmedEvent, OperatorReassignmentEvent, AssignExistingOperatorEvent, RemoveOperatorFromSiteEvent, CreateOperatorForSiteEvent } from '../sites-manager/sites-manager.component';

// Icons
import { faUser, faMapMarkerAlt, faBuilding, faUsers, faCheck, faExclamationTriangle, faSave, faArrowLeft, faArrowRight, faSpinner, faUserTie } from '@fortawesome/free-solid-svg-icons';

export interface CustomerFormSubmitEvent {
  customerData: CustomerFormData;
  isValid: boolean;
  hasChanges: boolean;
}

@Component({
  selector: 'app-customer-form',
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.scss']
})
export class CustomerFormComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() editMode: boolean = false;
  @Input() customerData: Customer | null = null;
  @Input() isLoading: boolean = false;
  @Input() showProgressIndicator: boolean = true;
  @Input() allowStepNavigation: boolean = true;
  @Input() validateOnChange: boolean = true;
  @Input() autoSave: boolean = false;
  @Input() autoSaveInterval: number = 30000; // 30 seconds

  @Output() formSubmit = new EventEmitter<CustomerFormSubmitEvent>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() stepChange = new EventEmitter<number>();
  @Output() validationChange = new EventEmitter<boolean>();

  @ViewChild(CustomerInfoComponent, { static: false }) customerInfoComponent!: CustomerInfoComponent;
  @ViewChild(RegionsManagerComponent, { static: false }) regionsManagerComponent!: RegionsManagerComponent;
  @ViewChild(SitesManagerComponent, { static: false }) sitesManagerComponent!: SitesManagerComponent;

  faUser = faUser;
  faMapMarkerAlt = faMapMarkerAlt;
  faBuilding = faBuilding;
  faUsers = faUsers;
  faCheck = faCheck;
  faExclamationTriangle = faExclamationTriangle;
  faSave = faSave;
  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faSpinner = faSpinner;
  faUserTie = faUserTie;

  customerForm: FormGroup;
  currentStep: number = 0;
  totalSteps: number = 4;
  isSaving: boolean = false;
  hasUnsavedChanges: boolean = false;
  
  stepValidation: { [key: number]: boolean } = {
    0: false, // Customer Info
    1: false, // Regions
    2: false, // Sites
    3: false  // Users
  };

  stepCompletion: { [key: number]: boolean } = {
    0: false,
    1: false,
    2: false,
    3: false
  };

  formData: CustomerFormData = {
    customer: {
      name: '',
      email: '',
      contactNo: ''
    },
    customerProfile: {
      name: ''
    },
    regions: [],
    sites: [],
    adminUsers: [],
    operatorUsers: [],
    isEditMode: this.editMode,
    hasUnsavedChanges: false,
    isValid: false,
    validationErrors: {}
  };

  steps = [
    {
      id: 0,
      title: 'Customer Information',
      description: 'Basic customer details and contact information',
      icon: this.faUser,
      component: 'customer-info'
    },
    {
      id: 1,
      title: 'Regions & Admins',
      description: 'Configure regions and assign admin users',
      icon: this.faMapMarkerAlt,
      component: 'regions-manager'
    },
    {
      id: 2,
      title: 'Sites & Operators',
      description: 'Set up sites and assign operators',
      icon: this.faBuilding,
      component: 'sites-manager'
    },
    {
      id: 3,
      title: 'Review & Confirm',
      description: 'Review all information before saving',
      icon: this.faCheck,
      component: 'review'
    }
  ];

  private destroy$ = new Subject<void>();
  private autoSaveTimer: any;

  constructor(
    private fb: FormBuilder,
    private customerStateService: CustomerStateService,
    private idGeneratorService: IdGeneratorService,
  ) {
    this.createForm();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormSubscriptions();
    this.setupAutoSave();
    this.loadInitialData();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initializeChildComponents();
      this.validateAllSteps();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearAutoSave();
  }

  private createForm(): void {
    this.customerForm = this.fb.group({
      customerInfo: this.fb.group({
        name: ['', [Validators.required, Validators.minLength(2)]],
        email: ['', [Validators.email]],
        phone: [''],
        address: ['']
      }),
      regions: this.fb.array([]),
      sites: this.fb.array([]),
      users: this.fb.group({
        admins: this.fb.array([]),
        operators: this.fb.array([])
      })
    });
  }

  private initializeForm(): void {
    if (this.customerData) {
      this.loadCustomerData(this.customerData);
    } else {
      this.setDefaultFormData();
    }
  }

  private loadCustomerData(customer: Customer): void {
    // Load customer basic info
    this.formData.customer = {
      id: customer.id,
      name: customer.name || '',
      email: customer.email || '',
      contactNo: customer.contactNo || ''
    };

    this.formData.customerProfile = {
      name: customer.name || ''
    };

    this.formData.isEditMode = true;
    
    // Subscribe to state changes
    this.customerStateService.regions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(regions => {
        this.formData.regions = regions;
        this.updateStepValidation(1);
      });

    this.customerStateService.sites$
      .pipe(takeUntil(this.destroy$))
      .subscribe(sites => {
        this.formData.sites = sites;
        this.updateStepValidation(2);
      });

    this.customerStateService.adminUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe(adminUsers => {
        this.formData.adminUsers = adminUsers;
        // Users are now managed inline in steps 1 and 2
      });

    this.customerStateService.operatorUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe(operatorUsers => {
        this.formData.operatorUsers = operatorUsers;
        // Users are now managed inline in steps 1 and 2
      });
  }

  private setDefaultFormData(): void {
    this.formData = {
      customer: {
        name: '',
        email: '',
        contactNo: ''
      },
      customerProfile: {
        name: ''
      },
      regions: [],
      sites: [],
      adminUsers: [],
      operatorUsers: [],
      isEditMode: false,
      hasUnsavedChanges: false,
      isValid: false,
      validationErrors: {}
    };
  }

  private setupFormSubscriptions(): void {
    // Watch for form changes
    this.customerForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.hasUnsavedChanges = true;
        this.validateCurrentStep();
        this.emitValidationChange();
      });

    this.customerForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.emitValidationChange();
      });
  }

  private setupAutoSave(): void {
    if (this.autoSave && this.editMode) {
      this.autoSaveTimer = setInterval(() => {
        if (this.hasUnsavedChanges && this.isFormValid()) {
          // this.saveForm(true); 
        }
      }, this.autoSaveInterval);
    }
  }

  private clearAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  private loadInitialData(): void {
    this.validateAllSteps();
  }

  private initializeChildComponents(): void {
    if (this.customerInfoComponent) {
      const customerInfoData = {
        customer: this.formData.customer,
        customerProfile: this.formData.customerProfile
      };
      this.customerInfoComponent.writeValue(customerInfoData);
    }
    if (this.regionsManagerComponent) {
      this.regionsManagerComponent.writeValue(this.formData.regions);
    }
    if (this.sitesManagerComponent) {
      this.sitesManagerComponent.writeValue({
        sites: this.formData.sites,
        regions: this.formData.regions
      });
    }

  }

  nextStep(): void {
    if (this.canNavigateToStep(this.currentStep + 1)) {
      this.setCurrentStep(this.currentStep + 1);
    }
  }

  previousStep(): void {
    if (this.currentStep > 0) {
      this.setCurrentStep(this.currentStep - 1);
    }
  }

  setCurrentStep(stepIndex: number): void {
    if (stepIndex >= 0 && stepIndex < this.totalSteps) {
      this.currentStep = stepIndex;
      this.stepChange.emit(stepIndex);
      this.validateCurrentStep();
    }
  }

  canNavigateToStep(stepIndex: number): boolean {
    if (!this.allowStepNavigation) return false;
    if (stepIndex < 0 || stepIndex >= this.totalSteps) return false;
    
    if (stepIndex > this.currentStep) {
      for (let i = 0; i < stepIndex; i++) {
        if (!this.stepValidation[i]) {
          return false;
        }
      }
    }
    
    return true;
  }

  private validateCurrentStep(): void {
    this.updateStepValidation(this.currentStep);
  }

  private validateAllSteps(): void {
    for (let i = 0; i < this.totalSteps; i++) {
      this.updateStepValidation(i);
    }
  }

  private updateStepValidation(stepIndex: number): void {
    let isValid = false;
    
    switch (stepIndex) {
      case 0: // Customer Info
        isValid = this.validateCustomerInfo();
        break;
      case 1: // Regions
        isValid = this.validateRegions();
        break;
      case 2: // Sites
        isValid = this.validateSites();
        break;
      case 3: // Review & Confirm
        isValid = true;
        break;
    }
    
    this.stepValidation[stepIndex] = isValid;
    this.stepCompletion[stepIndex] = isValid && this.hasStepData(stepIndex);
  }

  private validateCustomerInfo(): boolean {
    const customer = this.formData.customer;
    const customerProfile = this.formData.customerProfile;
    
    const hasValidName = !!(customer.name && customer.name.trim().length >= 2);
    const hasValidContact = !!(customer.contactNo && customer.contactNo.trim().length > 0);
    const hasValidEmail = !!(customer.email && customer.email.trim().length > 0 && this.isValidEmail(customer.email));
    const hasValidProfileName = !!(customerProfile.name && customerProfile.name.trim().length >= 2);
    
    return hasValidName && hasValidContact && hasValidEmail && hasValidProfileName;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private validateRegions(): boolean {
    return this.formData.regions.length > 0 && 
           this.formData.regions.every(region => !!(region.name && region.name.trim()));
  }

  private validateSites(): boolean {
    if (this.formData.sites.length === 0) return false;
    return this.formData.sites.every(site => 
      !!(site.name && site.name.trim() && site.region_id)
    );
  }


  private hasStepData(stepIndex: number): boolean {
    switch (stepIndex) {
      case 0:
        return !!(this.formData.customer.name && this.formData.customerProfile.name);
      case 1:
        return this.formData.regions.length > 0;
      case 2:
        return this.formData.sites.length > 0;
      case 3:
        return this.formData.adminUsers.length > 0 || this.formData.operatorUsers.length > 0;
      default:
        return false;
    }
  }

  isFormValid(): boolean {
    return Object.values(this.stepValidation).every(valid => valid);
  }

  private emitValidationChange(): void {
    this.validationChange.emit(this.isFormValid());
  }

  onCustomerInfoChange(event: any): void {
    if (event && event.data) {
      this.formData.customer = { ...this.formData.customer, ...event.data.customer };
      this.formData.customerProfile = { ...this.formData.customerProfile, ...event.data.customerProfile };
    }
    this.updateStepValidation(0);
    this.hasUnsavedChanges = true;
  }

  onRegionsChange(event: any): void {
    this.formData.regions = event.regions || [];
    this.updateStepValidation(1);
    this.hasUnsavedChanges = true;
    
    if (this.sitesManagerComponent) {
      this.sitesManagerComponent.regionsData = this.formData.regions;
    }

  }

  onRegionRemovalConfirmed(event: RegionRemovalConfirmedEvent): void {
    const { cascadeAnalysis } = event;
    
    // Remove dependent sites
    this.removeSitesInRegion(cascadeAnalysis.regionId);
    
    // Remove/update dependent admins
    this.removeAdminsInRegion(cascadeAnalysis.regionId);
    
    // Remove/update dependent operators
    this.removeOperatorsInSites(cascadeAnalysis.cascadeItems.sites);
    
    // Mark as having unsaved changes
    this.hasUnsavedChanges = true;
    
    // Update validation
    this.validateAllSteps();
  }

  onSitesChange(event: any): void {
    this.formData.sites = event.sites || [];
    this.updateStepValidation(2);
    this.hasUnsavedChanges = true;

  }



  onAssignExistingAdmin(event: any): void {
    const admin = this.formData.adminUsers.find(a => a.id === event.adminId);
    if (admin) {
      if (!admin.regionIds) {
        admin.regionIds = [];
        admin.regionNames = [];
      }
      
      if (!admin.regionIds.includes(event.regionId)) {
        admin.regionIds.push(event.regionId);
        admin.regionNames.push(event.regionName);
        
        this.hasUnsavedChanges = true;
        this.updateStepValidation(1); 
      }
    }
  }

  onAssignExistingOperator(event: AssignExistingOperatorEvent): void {
    const operator = this.formData.operatorUsers.find(o => String(o.id) === String(event.operatorId));
    if (operator) {
      if (!operator.siteIds) {
        operator.siteIds = [];
        operator.siteNames = [];
      }
      
      if (!operator.siteIds.some(id => String(id) === String(event.siteId))) {
        operator.siteIds.push(event.siteId);
        operator.siteNames.push(event.siteName);
        
        this.hasUnsavedChanges = true;
        this.updateStepValidation(2); 
        
        if (this.sitesManagerComponent) {
          this.sitesManagerComponent.operatorsData = this.formData.operatorUsers;
        }
      }
    }
  }

  onRemoveOperatorFromSite(event: RemoveOperatorFromSiteEvent): void {
    const operator = this.formData.operatorUsers.find(o => String(o.id) === String(event.operatorId));
    if (operator && operator.siteIds) {
      const siteIndex = operator.siteIds.findIndex(id => String(id) === String(event.siteId));
      if (siteIndex > -1) {
        operator.siteIds.splice(siteIndex, 1);
        if (operator.siteNames && operator.siteNames.length > siteIndex) {
          operator.siteNames.splice(siteIndex, 1);
        }
        
        this.hasUnsavedChanges = true;
        this.updateStepValidation(2); 
        
        if (this.sitesManagerComponent) {
          this.sitesManagerComponent.operatorsData = this.formData.operatorUsers;
        }
      }
    }
  }

  onCreateOperatorForSite(event: CreateOperatorForSiteEvent): void {
    const newOperator: any = {
      id: this.idGeneratorService.generateAlphanumericId(), 
      username: event.operatorData.username,
      password: event.operatorData.password,
      confirmPassword: event.operatorData.password,
      userDetails: {
        name: event.operatorData.fullName,
        email: event.operatorData.email,
        mobile: event.operatorData.mobile,
        emp_code: ''
      },
      roleIds: [],
      roleNames: [],
      regionIds: [],
      regionNames: [],
      siteIds: [event.siteId],
      siteNames: [event.siteName],
      userType: 'operator',
      isNew: true,
      isEditing: false,
      hasUnsavedChanges: false
    };
    
    this.formData.operatorUsers.push(newOperator);
    this.hasUnsavedChanges = true;
    this.updateStepValidation(2); 
    
    if (this.sitesManagerComponent) {
      this.sitesManagerComponent.operatorsData = this.formData.operatorUsers;
    }
  }

  // New enhanced site removal event handlers
  onSiteRemovalRequest(event: SiteRemovalRequestEvent): void {
    console.log('🚨 [Customer Form] Site removal requested:', {
      siteName: event.siteName,
      operatorsCount: event.assignedOperators.length,
      operators: event.assignedOperators.map(op => ({ id: op.id, username: op.username }))
    });
    
    // Optional: Add custom validation, warnings, or analytics here
    if (event.assignedOperators.length > 0) {
      console.warn(`⚠️ [Customer Form] Site "${event.siteName}" has ${event.assignedOperators.length} assigned operators that will be unassigned`);
    }
  }

  onSiteRemovalConfirmed(event: SiteRemovalConfirmedEvent): void {
    console.log('✅ [Customer Form] Site removal confirmed:', {
      siteName: event.siteName,
      siteId: event.siteId,
      operatorsAffected: event.operatorsToReassign.length
    });
    
    // Mark as having unsaved changes since site was removed
    this.hasUnsavedChanges = true;
    this.updateStepValidation(2);
    
    // Optional: Update backend, send analytics, etc.
    // Example: this.customerService.removeSite(event.siteId).subscribe();
  }

  onOperatorReassignment(event: OperatorReassignmentEvent): void {
    console.log('🔧 [Customer Form] Handling operator reassignment:', {
      operatorIds: event.operatorIds,
      fromSiteId: event.fromSiteId,
      action: event.action
    });
    
    if (event.action === 'unassign') {
      // Update operatorsData to remove site assignments
      event.operatorIds.forEach(operatorId => {
        const operator = this.formData.operatorUsers.find(op => String(op.id) === String(operatorId));
        if (operator && operator.siteIds) {
          console.log(`📍 [Customer Form] Before unassign - Operator ${operator.username} sites:`, operator.siteIds);
          
          // Remove the site ID from operator's assignments
          operator.siteIds = operator.siteIds.filter(
            siteId => String(siteId) !== String(event.fromSiteId)
          );
          
          // Also remove from siteNames if it exists
          if (operator.siteNames && operator.siteNames.length > 0) {
            const siteIndex = operator.siteNames.findIndex(name => 
              this.formData.sites.some(site => 
                String(site.id) === String(event.fromSiteId) && site.name === name
              )
            );
            if (siteIndex > -1) {
              operator.siteNames.splice(siteIndex, 1);
            }
          }
          
          console.log(`📍 [Customer Form] After unassign - Operator ${operator.username} sites:`, operator.siteIds);
        }
      });
      
      // Update the sites manager component with the updated operators data
      if (this.sitesManagerComponent) {
        this.sitesManagerComponent.operatorsData = this.formData.operatorUsers;
      }
      
      // Mark as having unsaved changes
      this.hasUnsavedChanges = true;
      this.updateStepValidation(2);
      
      console.log('✅ [Customer Form] Operator reassignment completed successfully');
    }
    
    // Handle 'reassign' action if you implement that functionality in the future
    if (event.action === 'reassign' && event.toSiteId) {
      event.operatorIds.forEach(operatorId => {
        const operator = this.formData.operatorUsers.find(op => String(op.id) === String(operatorId));
        if (operator && operator.siteIds) {
          // Remove old site ID
          operator.siteIds = operator.siteIds.filter(
            siteId => String(siteId) !== String(event.fromSiteId)
          );
          // Add new site ID
          operator.siteIds.push(event.toSiteId!);
          
          // Update site names as well
          if (operator.siteNames) {
            const newSite = this.formData.sites.find(site => String(site.id) === String(event.toSiteId));
            if (newSite) {
              operator.siteNames.push(newSite.name);
            }
          }
        }
      });
      
      if (this.sitesManagerComponent) {
        this.sitesManagerComponent.operatorsData = this.formData.operatorUsers;
      }
      
      this.hasUnsavedChanges = true;
      this.updateStepValidation(2);
    }
  }

  onRemoveAdminFromRegion(event: any): void {
    const admin = this.formData.adminUsers.find(a => a.id === event.adminId);
    if (admin && admin.regionIds) {
      const regionIndex = admin.regionIds.indexOf(event.regionId);
      if (regionIndex > -1) {
        admin.regionIds.splice(regionIndex, 1);
        admin.regionNames.splice(regionIndex, 1);
        
        this.hasUnsavedChanges = true;
        this.updateStepValidation(1); 
      }
    }
  }

  onCreateAdminForRegion(event: any): void {
    const newAdmin: any = {
      id: this.idGeneratorService.generateAlphanumericId(), 
      username: event.adminData.username,
      password: event.adminData.password,
      confirmPassword: event.adminData.password,
      userDetails: {
        name: event.adminData.fullName,
        email: event.adminData.email,
        mobile: event.adminData.mobile,
        emp_code: ''
      },
      roleIds: [],
      roleNames: [],
      regionIds: [event.regionId],
      regionNames: [event.regionName],
      siteIds: [],
      siteNames: [],
      userType: 'admin',
      isNew: true,
      isEditing: false,
      hasUnsavedChanges: false
    };
    
    this.formData.adminUsers.push(newAdmin);
    this.hasUnsavedChanges = true;
    this.updateStepValidation(1); 
  }

  // Admin edit/delete event handlers
  onEditAdminConfirmed(event: any): void {
    const admin = this.formData.adminUsers.find(a => String(a.id) === String(event.adminId));
    if (admin) {
      admin.username = event.adminData.username;
      admin.userDetails.name = event.adminData.fullName;
      admin.userDetails.email = event.adminData.email;
      admin.userDetails.mobile = event.adminData.mobile || '';
      admin.userDetails.emp_code = event.adminData.emp_code || '';
      
      this.hasUnsavedChanges = true;
      this.updateStepValidation(1);
      
      // Update the regions manager component with the updated admin data
      if (this.regionsManagerComponent) {
        this.regionsManagerComponent.regionsData = this.formData.regions;
      }
    }
  }

  onDeleteAdminConfirmed(event: any): void {
    const adminIndex = this.formData.adminUsers.findIndex(a => String(a.id) === String(event.adminId));
    if (adminIndex > -1) {
      this.formData.adminUsers.splice(adminIndex, 1);
      
      this.hasUnsavedChanges = true;
      this.updateStepValidation(1);
      
      // Update the regions manager component with the updated admin data
      if (this.regionsManagerComponent) {
        this.regionsManagerComponent.regionsData = this.formData.regions;
      }
    }
  }

  // Operator edit/delete event handlers
  onEditOperatorConfirmed(event: any): void {
    const operator = this.formData.operatorUsers.find(o => String(o.id) === String(event.operatorId));
    if (operator) {
      operator.username = event.operatorData.username;
      operator.userDetails.name = event.operatorData.fullName;
      operator.userDetails.email = event.operatorData.email;
      operator.userDetails.mobile = event.operatorData.mobile || '';
      operator.userDetails.emp_code = event.operatorData.emp_code || '';
      
      this.hasUnsavedChanges = true;
      this.updateStepValidation(2);
      
      // Update the sites manager component with the updated operator data
      if (this.sitesManagerComponent) {
        this.sitesManagerComponent.operatorsData = this.formData.operatorUsers;
      }
    }
  }

  onDeleteOperatorConfirmed(event: any): void {
    const operatorIndex = this.formData.operatorUsers.findIndex(o => String(o.id) === String(event.operatorId));
    if (operatorIndex > -1) {
      this.formData.operatorUsers.splice(operatorIndex, 1);
      
      this.hasUnsavedChanges = true;
      this.updateStepValidation(2);
      
      // Update the sites manager component with the updated operator data
      if (this.sitesManagerComponent) {
        this.sitesManagerComponent.operatorsData = this.formData.operatorUsers;
      }
    }
  }

  saveForm(isAutoSave: boolean = false): void {
    if (!this.isFormValid() && !isAutoSave) {
      this.validateAllSteps();
      return;
    }

    this.isSaving = true;
    
    const submitData: CustomerFormSubmitEvent = {
      customerData: this.formData,
      isValid: this.isFormValid(),
      hasChanges: this.hasUnsavedChanges
    };

    this.formSubmit.emit(submitData);
    
    setTimeout(() => {
      this.isSaving = false;
      if (!isAutoSave) {
        this.hasUnsavedChanges = false;
      }
    }, 1000);
  }

  cancelForm(): void {
    this.formCancel.emit();
  }

  resetForm(): void {
    this.customerForm.reset();
    this.setDefaultFormData();
    this.currentStep = 0;
    this.hasUnsavedChanges = false;
    this.validateAllSteps();
  }

  getCustomerInfoData(): any {
    return {
      customer: this.formData.customer,
      customerProfile: this.formData.customerProfile
    };
  }

  getStepTitle(stepIndex: number): string {
    return this.steps[stepIndex] ? this.steps[stepIndex].title : '';
  }

  getStepDescription(stepIndex: number): string {
    return this.steps[stepIndex] ? this.steps[stepIndex].description : '';
  }

  getStepIcon(stepIndex: number) {
    return this.steps[stepIndex] ? this.steps[stepIndex].icon : this.faUser;
  }

  isStepValid(stepIndex: number): boolean {
    return this.stepValidation[stepIndex] || false;
  }

  isStepComplete(stepIndex: number): boolean {
    return this.stepCompletion[stepIndex] || false;
  }

  canProceedToNext(): boolean {
    return this.canNavigateToStep(this.currentStep + 1);
  }

  canGoToPrevious(): boolean {
    return this.currentStep > 0;
  }

  isLastStep(): boolean {
    return this.currentStep === this.totalSteps - 1;
  }

  isFirstStep(): boolean {
    return this.currentStep === 0;
  }

  markAllAsTouched(): void {
    this.customerForm.markAllAsTouched();
    
    if (this.customerInfoComponent) {
      this.customerInfoComponent.markAllAsTouched();
    }
    if (this.regionsManagerComponent) {
      this.regionsManagerComponent.markAllAsTouched();
    }
    if (this.sitesManagerComponent) {
      this.sitesManagerComponent.markAllAsTouched();
    }

  }

  private removeSitesInRegion(regionId: number | string | null): void {
    this.formData.sites = this.formData.sites.filter(site => 
      String(site.region_id) !== String(regionId)
    );
    
    if (this.sitesManagerComponent) {
      this.sitesManagerComponent.sitesData = this.formData.sites;
    }
  }

  private removeAdminsInRegion(regionId: number | string | null): void {
    // Remove admins who are only assigned to this region
    this.formData.adminUsers = this.formData.adminUsers.filter(admin => {
      if (admin.regionIds.length === 1 && String(admin.regionIds[0]) === String(regionId)) {
        return false; // Remove admin
      } else {
        // Remove region from admin's region list
        admin.regionIds = admin.regionIds.filter(id => String(id) !== String(regionId));
        admin.regionNames = admin.regionNames.filter((name, index) => {
          const regionIdAtIndex = admin.regionIds[index];
          return regionIdAtIndex !== undefined && String(regionIdAtIndex) !== String(regionId);
        });
        return true; // Keep admin
      }
    });
  }

  private removeOperatorsInSites(sitesToRemove: any[]): void {
    const siteIdsToRemove = sitesToRemove.map(site => String(site.id));
    
    this.formData.operatorUsers = this.formData.operatorUsers.filter(operator => {
      // Remove operators who are only assigned to sites in this region
      const remainingSites = operator.siteIds.filter(siteId => 
        !siteIdsToRemove.includes(String(siteId))
      );
      
      if (remainingSites.length === 0) {
        return false; // Remove operator
      } else {
        // Update operator's site assignments
        operator.siteIds = remainingSites;
        operator.siteNames = operator.siteNames.filter((name, index) => {
          const siteIdAtIndex = operator.siteIds[index];
          return siteIdAtIndex !== undefined && remainingSites.includes(siteIdAtIndex);
        });
        return true; // Keep operator
      }
    });
  }

  getCurrentFormData(): CustomerFormData {
    return { ...this.formData };
  }

  setFormData(data: CustomerFormData): void {
    this.formData = { ...data };
    this.initializeChildComponents();
    this.validateAllSteps();
  }

  getValidationSummary(): { [key: string]: boolean } {
    return {
      customerInfo: this.stepValidation[0],
      regions: this.stepValidation[1],
      sites: this.stepValidation[2],
      users: this.stepValidation[3],
      overall: this.isFormValid()
    };
  }
}